<template>
  <div>
    <h4 class="mb-3">Campos de configuración</h4>
    <v-row>
      <v-col
        v-for="field in contextFields"
        :key="field.key"
        cols="12"
        :md="field.type === 'textarea' ? 12 : 6"
      >
        <!-- Campo de texto -->
        <v-text-field
          v-if="field.type === 'string'"
          :value="localValue[field.key]"
          @input="updateField(field.key, $event)"
          :label="field.ui.label"
          :placeholder="field.ui.placeholder"
          :hint="field.ui.helpText"
          :rules="getFieldRules(field)"
          :required="field.required"
          persistent-hint
          outlined
          dense
        >
          <template v-slot:append-outer v-if="field.ui.helpText">
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small color="grey">
                  mdi-help-circle
                </v-icon>
              </template>
              <span>{{ field.ui.helpText }}</span>
            </v-tooltip>
          </template>
        </v-text-field>

        <!-- Campo de password -->
        <v-text-field
          v-else-if="field.type === 'password'"
          autocomplete="off"
          :value="
            showPassword[field.key]
              ? localValue[field.key]
              : localValue[field.key]?.replace(/./g, '*')
          "
          @input="updateField(field.key, $event)"
          :label="field.ui.label"
          :placeholder="field.ui.placeholder"
          :hint="field.ui.helpText"
          :rules="getFieldRules(field)"
          :required="field.required"
          type="text"
          :append-icon="showPassword[field.key] ? 'mdi-eye' : 'mdi-eye-off'"
          @click:append="togglePassword(field.key)"
          persistent-hint
          outlined
          dense
        >
          <template v-slot:append-outer v-if="field.ui.helpText">
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small color="grey">
                  mdi-help-circle
                </v-icon>
              </template>
              <span>{{ field.ui.helpText }}</span>
            </v-tooltip>
          </template>
        </v-text-field>

        <!-- Campo de número -->
        <v-text-field
          v-else-if="field.type === 'number'"
          :value="localValue[field.key]"
          @input="updateField(field.key, $event)"
          :label="field.ui.label"
          :placeholder="field.ui.placeholder"
          :hint="field.ui.helpText"
          :rules="getFieldRules(field)"
          :required="field.required"
          type="number"
          persistent-hint
          outlined
          dense
        >
          <template v-slot:append-outer v-if="field.ui.helpText">
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small color="grey">
                  mdi-help-circle
                </v-icon>
              </template>
              <span>{{ field.ui.helpText }}</span>
            </v-tooltip>
          </template>
        </v-text-field>

        <!-- Campo de textarea -->
        <v-textarea
          v-else-if="field.type === 'textarea'"
          :value="localValue[field.key]"
          @input="updateField(field.key, $event)"
          :label="field.ui.label"
          :placeholder="field.ui.placeholder"
          :hint="field.ui.helpText"
          :rules="getFieldRules(field)"
          :required="field.required"
          persistent-hint
          outlined
          dense
          rows="3"
        >
          <template v-slot:append-outer v-if="field.ui.helpText">
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small color="grey">
                  mdi-help-circle
                </v-icon>
              </template>
              <span>{{ field.ui.helpText }}</span>
            </v-tooltip>
          </template>
        </v-textarea>

        <!-- Campo de boolean/switch -->
        <v-switch
          v-else-if="field.type === 'boolean'"
          :input-value="localValue[field.key]"
          @change="updateField(field.key, $event)"
          :label="field.ui.label"
          :hint="field.ui.helpText"
          persistent-hint
          color="success"
          inset
        ></v-switch>

        <!-- Campo de select -->
        <v-select
          v-else-if="field.type === 'select' && field.options"
          :value="localValue[field.key]"
          @input="updateField(field.key, $event)"
          :items="field.options"
          :label="field.ui.label"
          :placeholder="field.ui.placeholder"
          :hint="field.ui.helpText"
          :rules="getFieldRules(field)"
          :required="field.required"
          persistent-hint
          outlined
          dense
        >
          <template v-slot:append-outer v-if="field.ui.helpText">
            <v-tooltip bottom max-width="300">
              <template v-slot:activator="{ on, attrs }">
                <v-icon v-bind="attrs" v-on="on" small color="grey">
                  mdi-help-circle
                </v-icon>
              </template>
              <span>{{ field.ui.helpText }}</span>
            </v-tooltip>
          </template>
        </v-select>
      </v-col>
    </v-row>
  </div>
</template>

<script>
export default {
  name: "WebhookContextFields",
  props: {
    value: {
      type: Object,
      default: () => ({})
    },
    contextFields: {
      type: Array,
      required: true
    }
  },
  data() {
    return {
      localValue: { ...this.value },
      showPassword: {}
    };
  },
  watch: {
    value: {
      handler(newVal) {
        this.localValue = { ...newVal };
      },
      deep: true
    }
  },
  methods: {
    updateField(key, value) {
      this.localValue[key] = value;
      this.$emit("input", { ...this.localValue });
    },
    togglePassword(key) {
      this.$set(this.showPassword, key, !this.showPassword[key]);
    },
    getFieldRules(field) {
      const rules = [];

      // Validación de requerido
      if (field.required) {
        rules.push(v => !!v || `${field.ui.label} es requerido`);
      }

      // Validación de patrón
      if (field.validation?.pattern) {
        const pattern = new RegExp(field.validation.pattern);
        rules.push(
          v =>
            !v ||
            pattern.test(v) ||
            field.validation.error_message ||
            "Formato inválido"
        );
      }

      // Validación de longitud mínima
      if (field.validation?.min !== undefined) {
        rules.push(
          v =>
            !v ||
            v.length >= field.validation.min ||
            `Debe tener al menos ${field.validation.min} caracteres`
        );
      }

      // Validación de longitud máxima
      if (field.validation?.max !== undefined) {
        rules.push(
          v =>
            !v ||
            v.length <= field.validation.max ||
            `No debe exceder ${field.validation.max} caracteres`
        );
      }

      // Validación de número mínimo
      if (
        field.type === "number" &&
        field.validation?.min_value !== undefined
      ) {
        rules.push(
          v =>
            !v ||
            Number(v) >= field.validation.min_value ||
            `Debe ser al menos ${field.validation.min_value}`
        );
      }

      // Validación de número máximo
      if (
        field.type === "number" &&
        field.validation?.max_value !== undefined
      ) {
        rules.push(
          v =>
            !v ||
            Number(v) <= field.validation.max_value ||
            `No debe exceder ${field.validation.max_value}`
        );
      }

      return rules;
    }
  }
};
</script>
